import { useState, useEffect } from "react";
import "./admin.scss";
import TransferTable from "../../components/TransferTable";
import { useSelector } from "react-redux";
import Coupon from "../../components/classes/Coupon";
import CouponModal from "../../components/CouponModal";
import {
  sendOrderConfirmationEmail,
  getEvents,
  getEventRegistrations,
  createGratuityPaymentLink,
  sendGratuityFollowUpEmail,
} from "../../components/api/api";
import { formatCurrency } from "../../utility/UtilityFuncs";
import Button from "../../components/Button/Button";

const Admin = () => {
  const [pendingEtransfers, setPendingEtransers] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);
  const [isCouponModalVisible, setCouponModalVisible] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventName, setSelectedEventName] = useState("");
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [sendingGratuity, setSendingGratuity] = useState(false);
  const [gratuityFeedback, setGratuityFeedback] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmFeedback, setConfirmFeedback] = useState(null); // { type: 'success' | 'error', message: string }
  let coupon = new Coupon("", "");
  const [couponFormData, setCouponFormData] = useState(coupon);
  const showConfirmModal = () => setConfirmModalVisible(true);
  const hideConfirmModal = () => {
    setConfirmModalVisible(false);
    setConfirmFeedback(null);
  };
  const showCouponModal = () => setCouponModalVisible(true);
  const hideCouponModal = () => setCouponModalVisible(false);

  const { storeData } = useSelector((state) => ({
    storeData: state.storeSlice.googleSheetData,
  }));

  useEffect(() => {
    const fetchEtransfers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/google-sheets/pending-etransfers`,
        );
        const data = await response.json();
        const filtered = Array.isArray(data)
          ? data.filter(
              (order) =>
                !order["Items Purchased"]?.toLowerCase().includes("night"),
            )
          : [];
        console.log("Pending Etransfers", filtered);
        setPendingEtransers(filtered);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchEtransfers();
  }, []);

  useEffect(() => {
    getEvents(true).then((data) => setEvents(Array.isArray(data) ? data : []));
  }, []);

  useEffect(() => {
    if (selectedEventName) {
      getEventRegistrations(selectedEventName).then(setEventRegistrations);
    } else {
      setEventRegistrations([]);
    }
  }, [selectedEventName]);

  const confirmOrders = async () => {
    setIsConfirming(true);
    setConfirmFeedback(null);

    // remove order from Pending Etransfers on Google Sheets
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/google-sheets/confirm-orders`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(selectedRows),
        },
      );

      if (response.ok) {
        const emailResults = [];
        const emailPromises = selectedRows.map(async (order) => {
          // Parse the total from the order (remove currency formatting if present)
          const totalString = order["Total Order $"] || order["Total"] || "0";
          const total =
            parseFloat(totalString.toString().replace(/[^0-9.-]/g, "")) || 0;

          // For e-transfer orders, we need to construct the costs object
          // Since we don't have the breakdown stored, we'll work backwards from total
          // Total = subtotalAfterCoupons + tax (14%)
          // So: total = subtotalAfterCoupons * 1.14
          // Therefore: subtotalAfterCoupons = total / 1.14
          const subtotalAfterCoupons = total / 1.14;
          const tax = total - subtotalAfterCoupons;

          // Construct costs object - credit is always 0 for e-transfer orders
          const costs = {
            subtotal: formatCurrency(subtotalAfterCoupons),
            tax: formatCurrency(tax),
            credit: formatCurrency(0), // E-transfer orders don't have credit card fee
            total: formatCurrency(total),
            couponSavings: formatCurrency(0), // We don't have this data stored, defaulting to 0
          };

          const emailResult = await sendOrderConfirmationEmail({
            name: order["Name"],
            emailAddress: order["Email"],
            costs: costs,
            itemsPurchased: order["Items Purchased"],
            pickupLocation: order["Pickup Location"],
            orderId: order["Order ID"],
          });

          return {
            orderId: order["Order ID"],
            email: order["Email"],
            name: order["Name"],
            success: emailResult,
          };
        });

        const results = await Promise.all(emailPromises);
        const successful = results.filter((r) => r.success).length;
        const failed = results.filter((r) => !r.success).length;

        // Remove confirmed orders from local state
        setPendingEtransers((prevData) =>
          prevData.filter(
            (order) =>
              !selectedRows.some(
                (selectedOrder) =>
                  selectedOrder.Email === order.Email &&
                  selectedOrder["Total Order $"] === order["Total Order $"],
              ),
          ),
        );
        setSelectedRows([]); // Reset selected rows

        setIsConfirming(false);

        // Set feedback message
        if (failed === 0) {
          setConfirmFeedback({
            type: "success",
            message: `Successfully confirmed ${successful} order${successful !== 1 ? "s" : ""} and sent confirmation emails!`,
          });
        } else {
          setConfirmFeedback({
            type: "error",
            message: `Confirmed ${successful} order${successful !== 1 ? "s" : ""}, but failed to send ${failed} email${failed !== 1 ? "s" : ""}. Check console for details.`,
          });
        }

        // Auto-hide modal after showing feedback
        setTimeout(() => {
          hideConfirmModal();
        }, 3000);
      } else {
        setIsConfirming(false);
        setConfirmFeedback({
          type: "error",
          message: "Failed to confirm orders. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error confirming orders:", error);
      setIsConfirming(false);
      setConfirmFeedback({
        type: "error",
        message: `Error confirming orders: ${error.message || "Unknown error"}`,
      });
    }
  };

  const sendGratuityEmails = async () => {
    if (!selectedEventName || eventRegistrations.length === 0) return;
    const event = events.find(
      (e) => (e.NAME || e.name || "").toString().trim() === selectedEventName,
    );
    const eventTitle = event?.NAME || event?.name || "Event";
    setSendingGratuity(true);
    setGratuityFeedback(null);
    let success = 0;
    let failed = 0;
    for (const reg of eventRegistrations) {
      const regName = reg["NAME"] || reg.name;
      const regEmail = reg["EMAIL"] || reg.email;
      const paymentLink = await createGratuityPaymentLink(
        selectedEventName,
        regName,
        regEmail,
      );
      const sent = await sendGratuityFollowUpEmail({
        name: regName,
        email: regEmail,
        eventTitle,
        paymentLink: paymentLink || "(link unavailable)",
      });
      sent ? success++ : failed++;
    }
    setGratuityFeedback(
      failed === 0
        ? { type: "success", message: `Sent ${success} gratuity email(s).` }
        : { type: "error", message: `Sent ${success}, failed ${failed}.` },
    );
    setSendingGratuity(false);
  };

  return (
    <div className="content-container">
      <div className="admin-content">
        <h1 className="admin-title">Admin</h1>
        {isLoggedIn ? (
          <div className="logged-in-content-container">
            <Button
              className={
                selectedRows.length <= 0
                  ? "submit-button"
                  : "submit-button button-animation"
              }
              disabled={selectedRows.length <= 0}
              onClick={showConfirmModal}
            >
              Confirm Orders
            </Button>
            <div className="confirmEtransfers">
              <Modal
                show={isConfirmModalVisible}
                onClose={hideConfirmModal}
                onConfirm={confirmOrders}
                title="Confirm Orders"
                message="Are you sure you want to confirm these orders?"
                actionButton="Confirm"
                isConfirming={isConfirming}
                feedback={confirmFeedback}
              />
              <TransferTable
                key={pendingEtransfers.length}
                data={pendingEtransfers}
                onSelectedRowsChange={setSelectedRows}
              />
            </div>
            <div className="coupon-container">
              <h2>Coupons</h2>
              <Button className="new-coupon-btn" onClick={showCouponModal}>
                Create New Coupon
              </Button>
              {isCouponModalVisible && (
                <CouponModal
                  storeData={storeData}
                  formData={couponFormData}
                  setFormData={setCouponFormData}
                  onClose={hideCouponModal}
                  show={isCouponModalVisible}
                />
              )}
            </div>
            <div className="events-gratuity-container">
              <h2>Event Gratuity Emails</h2>
              <p className="events-gratuity-desc">
                Send follow-up gratuity payment links to event registrants.
              </p>
              <select
                value={selectedEventName}
                onChange={(e) => setSelectedEventName(e.target.value)}
                className="events-select"
              >
                <option value="">Select an event</option>
                {events.map((ev) => (
                  <option
                    key={ev.NAME || ev.name}
                    value={(ev.NAME || ev.name || "").toString().trim()}
                  >
                    {ev.NAME || ev.name}
                  </option>
                ))}
              </select>
              {eventRegistrations.length > 0 && (
                <>
                  <p className="registrations-count">
                    {eventRegistrations.length} registration(s)
                  </p>
                  <Button
                    onClick={sendGratuityEmails}
                    disabled={sendingGratuity}
                  >
                    {sendingGratuity ? "Sending..." : "Send Gratuity Emails"}
                  </Button>
                  {gratuityFeedback && (
                    <p className={`gratuity-feedback ${gratuityFeedback.type}`}>
                      {gratuityFeedback.message}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <Login onLogin={setIsLoggedIn} />
        )}
      </div>
    </div>
  );
};

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = () => {
    if (
      username === process.env.REACT_APP_ADMIN_USERNAME &&
      password === process.env.REACT_APP_ADMIN_PASSWORD
    ) {
      onLogin(true);
    } else {
      setError("Invalid username or password");
    }
  };
  return (
    <div className="credential-container">
      <input
        type="text"
        placeholder="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Button onClick={handleLogin}>Login</Button>
      {error && <p>{error}</p>}
    </div>
  );
};

export const Modal = ({
  show,
  onClose,
  onConfirm,
  title,
  message,
  actionButton,
  isConfirming = false,
  feedback = null,
}) => {
  if (!show) {
    return null;
  }

  return (
    <div className="confirm-modal">
      <div className="confirm-modal-content">
        <div className="confirm-modal-header">
          <h4 className="confirm-modal-title">{title}</h4>
        </div>
        <div className="confirm-modal-body">
          <p>{message}</p>
          {feedback && (
            <div className={`confirm-feedback ${feedback.type}`}>
              {feedback.type === "success" && (
                <span className="feedback-icon">✓</span>
              )}
              {feedback.type === "error" && (
                <span className="feedback-icon">✗</span>
              )}
              <p>{feedback.message}</p>
            </div>
          )}
        </div>
        <div className="confirm-modal-footer">
          <button
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isConfirming}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`btn btn-primary ${isConfirming ? "confirming" : ""}`}
            disabled={isConfirming}
          >
            {isConfirming ? (
              <div className="button-loading-content">
                <div className="button-spinner"></div>
                <span>Confirming...</span>
              </div>
            ) : (
              actionButton
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin;
